import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Department, DepartmentLayout, DriveConnection } from '../types';
import { mockDepartments } from '../data/mockData';

interface DepartmentsState {
  departments: Department[];
  loading:     boolean;
  replaceAll:  (departments: Department[]) => void;
  add:         (dept: Department) => void;
  update:      (id: string, patch: Partial<Department>) => void;
  remove:      (id: string) => void;
  getBySlug:   (slug: string) => Department | undefined;
  getById:     (id: string)   => Department | undefined;
  saveLayout:  (id: string, layout: DepartmentLayout) => void;
  setStatus:   (id: string, status: Department['status']) => void;
  setDrive:    (id: string, drive: DriveConnection | undefined) => void;
  adjustUsage: (id: string, byteDelta: number, documentDelta?: number) => void;
}

export const useDepartmentsStore = create<DepartmentsState>()(
  persist(
    (set, get) => ({
      departments: mockDepartments,
      loading:     false,

      replaceAll: departments => set({ departments }),

      add: (dept) =>
        set(s => ({ departments: [...s.departments, dept] })),

      update: (id, patch) =>
        set(s => ({
          departments: s.departments.map(d =>
            d.id === id ? { ...d, ...patch, updatedAt: new Date().toISOString() } : d
          ),
        })),

      remove: (id) =>
        set(s => ({ departments: s.departments.filter(d => d.id !== id) })),

      getBySlug: (slug) => get().departments.find(d => d.slug === slug),
      getById:   (id)   => get().departments.find(d => d.id === id),

      saveLayout: (id, layout) =>
        set(s => ({
          departments: s.departments.map(d =>
            d.id === id ? { ...d, layout, updatedAt: new Date().toISOString() } : d
          ),
        })),

      setStatus: (id, status) => set(s => ({
        departments: s.departments.map(d => d.id === id
          ? { ...d, status, updatedAt: new Date().toISOString() }
          : d),
      })),

      setDrive: (id, drive) => set(s => ({
        departments: s.departments.map(d => d.id === id
          ? { ...d, drive, updatedAt: new Date().toISOString() }
          : d),
      })),

      adjustUsage: (id, byteDelta, documentDelta = 0) => set(s => ({
        departments: s.departments.map(d => d.id === id ? {
          ...d,
          quota: { ...d.quota, usedBytes: Math.max(0, d.quota.usedBytes + byteDelta) },
          documentCount: Math.max(0, d.documentCount + documentDelta),
          updatedAt: new Date().toISOString(),
        } : d),
      })),
    }),
    { name: 'masar-departments' }
  )
);
